export interface VideoEmbedResult {
  embedUrl: string;
  provider: string;
}

const VIMEO_RE = /\/(?:video\/)?(\d+)/;
const DAILYMOTION_RE = /\/video\/([a-zA-Z0-9]+)/;
const LOOM_RE = /\/share\/([a-zA-Z0-9]+)/;

const parseYouTubeEmbed = (
  host: string,
  pathname: string,
  searchParams: URLSearchParams
): VideoEmbedResult | null => {
  const videoId =
    host === "youtu.be"
      ? pathname.slice(1).split("/")[0] || null
      : searchParams.get("v");
  return videoId
    ? {
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        provider: "youtube"
      }
    : null;
};

const parseVimeoEmbed = (pathname: string): VideoEmbedResult | null => {
  const match = VIMEO_RE.exec(pathname);
  const videoId = match?.[1];
  return videoId
    ? {
        embedUrl: `https://player.vimeo.com/video/${videoId}`,
        provider: "vimeo"
      }
    : null;
};

const parseDailymotionEmbed = (pathname: string): VideoEmbedResult | null => {
  const match = DAILYMOTION_RE.exec(pathname);
  const videoId = match?.[1];
  return videoId
    ? {
        embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
        provider: "dailymotion"
      }
    : null;
};

const parseLoomEmbed = (pathname: string): VideoEmbedResult | null => {
  const match = LOOM_RE.exec(pathname);
  const videoId = match?.[1];
  return videoId
    ? { embedUrl: `https://www.loom.com/embed/${videoId}`, provider: "loom" }
    : null;
};

/**
 * Parses a video share URL and returns the canonical embed URL for supported
 * providers (YouTube, Vimeo, Dailymotion, Loom). Returns null for unsupported URLs.
 * @param url - Share URL (e.g. youtube.com/watch?v=ID, vimeo.com/ID)
 * @returns Embed URL and provider name, or null if unsupported
 */
export const parseVideoEmbedUrl = (url: string): VideoEmbedResult | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase();
    const pathname = parsed.pathname;

    if (host.includes("youtube.com") || host === "youtu.be") {
      const result = parseYouTubeEmbed(host, pathname, parsed.searchParams);
      if (result) return result;
    }
    if (host.includes("vimeo.com")) {
      const result = parseVimeoEmbed(pathname);
      if (result) return result;
    }
    if (host.includes("dailymotion.com")) {
      const result = parseDailymotionEmbed(pathname);
      if (result) return result;
    }
    if (host.includes("loom.com")) {
      const result = parseLoomEmbed(pathname);
      if (result) return result;
    }

    return null;
  } catch {
    return null;
  }
};
